import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLoad } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Executive, Sales, Operations, Finance, Inventory, Placeholder, Headcount, Head, MaintenanceSection } from "./Sections";

const META = {
  executive: ["Executive overview", "Revenue, cash, receivables, payables and margins"],
  sales: ["Sales", "Pipeline, conversion and customers"],
  operations: ["Operations", "Production WIP, queues and delivery performance"],
  finance: ["Finance", "Billing, approvals, tax and reconciliation"],
  inventory: ["Inventory", "Stock health and consumption"],
  hr: ["HR & payroll", "Attendance, overtime, leave and headcount"],
  maintenance: ["Maintenance", "Tickets, preventive maintenance and downtime"],
};

function Body({ s, cur }) {
  const d = s.data;
  if (s.key === "executive") return <Executive d={d} cur={cur} />;
  if (s.key === "sales") return <Sales d={d} cur={cur} />;
  if (s.key === "operations") return <Operations d={d} />;
  if (s.key === "finance") return <Finance d={d} cur={cur} />;
  if (s.key === "inventory") return <Inventory d={d} />;
  if (s.key === "hr") return <Placeholder testId="hr-placeholder" title="HR & Payroll" enabled={s.enabled} widgets={["Attendance compliance", "Overtime hotspots", "Leave requests pending", "Headcount by department"]}>{d && <Headcount data={d} />}</Placeholder>;
  if (d) return <MaintenanceSection d={d} />;
  return <Placeholder testId="maintenance-placeholder" title="Maintenance" enabled={s.enabled} widgets={["Open breakdowns", "Tasks due (7 days)", "Downtime this month", "Assets under maintenance"]} />;
}

function useActiveSection(keys) {
  const [active, setActive] = useState(keys[0]);
  const sig = keys.join(",");
  useEffect(() => {
    if (!keys.length) return undefined;
    setActive(keys[0]);
    const obs = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActive(vis[0].target.id.replace("dash-", ""));
    }, { rootMargin: "-80px 0px -60% 0px" });
    keys.forEach((k) => { const el = document.getElementById(`dash-${k}`); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  return [active, setActive];
}

export default function UnifiedDashboard() {
  const [d, reload, st] = useLoad("/dashboards/unified");
  const { user } = useAuth();
  const sections = (d?.sections || []).filter((s) => META[s.key]);
  const [active, setActive] = useActiveSection(sections.map((s) => s.key));
  if (!d && st.error) return (
    <div className="py-20 text-center" data-testid="dashboard-error"><p className="mb-3 text-sm text-slate-500">Couldn't load dashboard.</p>
      <Button variant="outline" data-testid="dashboard-retry" onClick={reload}>Retry</Button></div>);
  if (!d) return <div className="py-20 text-center text-sm text-slate-400">Loading dashboard…</div>;
  return (
    <>
      <PageHeader title={`Good day, ${user?.name?.split(" ")[0] || ""}`} subtitle={`Your ${(d.role || "team").replace("_", " ")} dashboard · sections shown match your role and your plan's modules.`}>
        <Button variant="outline" data-testid="dashboard-refresh-button" onClick={reload}><RefreshCw className="mr-1 h-4 w-4" />Refresh</Button>
      </PageHeader>
      <div className="sticky top-14 z-10 -mx-1 bg-slate-50/90 px-1 py-2 backdrop-blur md:top-0">
        <nav className="po-segmented max-w-full" data-testid="dashboard-section-nav">
          {sections.map((s) => <a key={s.key} href={`#dash-${s.key}`} onClick={() => setActive(s.key)} data-active={active === s.key} aria-current={active === s.key ? "true" : undefined} data-testid={`dashboard-jump-${s.key}`} className="po-segment">{META[s.key][0]}</a>)}
        </nav>
      </div>
      {sections.length === 0 && <p className="text-sm text-slate-500" data-testid="dashboard-empty">No dashboard sections are available for your role.</p>}
      <div className="space-y-8">
        {sections.map((s) => (
          <section key={s.key} id={`dash-${s.key}`} data-testid={`dashboard-section-${s.key}`} className="scroll-mt-28 space-y-4">
            <Head title={META[s.key][0]} sub={META[s.key][1]} />
            <Body s={s} cur={d.currency} />
          </section>
        ))}
      </div>
    </>
  );
}
